import { Link } from "react-router-dom";
import { Zap, Upload, Wallet, Users, ArrowRight, Sparkles, ShieldCheck, TrendingDown, Power, Wifi, Smartphone, GraduationCap, Tv, Landmark, Home } from "lucide-react";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import Logo from "../components/Logo";

const categories = [
  { icon: Zap, name: "Electricity", soon: false },
  { icon: Power, name: "Generators", soon: false },
  { icon: Wifi, name: "Internet", soon: false },
  { icon: Smartphone, name: "Mobile", soon: false },
  { icon: ShieldCheck, name: "Insurance", soon: false },
  { icon: GraduationCap, name: "Education", soon: false },
  { icon: Tv, name: "Subscriptions", soon: true },
  { icon: Landmark, name: "Government & Fees", soon: true },
  { icon: Home, name: "Housing", soon: true },
];

const steps = [
  { icon: Users, title: "Register", desc: "Create your free account in seconds. No credit card needed." },
  { icon: Upload, title: "Add Bill", desc: "Snap, upload or enter any bill. Our AI reads it for you." },
  { icon: Wallet, title: "Pay & Earn", desc: "Pay with a discount, earn reward points, and switch to save more." },
];

const features = [
  { icon: Sparkles, title: "AI Bill Reader", desc: "Extracts provider, units, amount & tariff automatically." },
  { icon: TrendingDown, title: "Savings Insights", desc: "Personalized tips to cut your electricity spend." },
  { icon: Users, title: "Refer & Earn", desc: "Invite friends — you both earn cash credits." },
  { icon: ShieldCheck, title: "Secure Payouts", desc: "Withdraw your wallet balance once you hit the threshold." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Logo />
        <div className="flex items-center gap-2">
          <Link to="/login"><Button variant="ghost" data-testid="header-login-btn" className="font-semibold">Log in</Button></Link>
          <Link to="/register"><Button data-testid="header-signup-btn" className="rounded-full bg-slate-900 hover:bg-slate-800 font-semibold">Get Started</Button></Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-1.5 rounded-full text-xs font-bold tracking-wide uppercase mb-6">
              <Sparkles className="w-4 h-4" /> HARMONEYS · Enjoy The Difference
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-none text-slate-900">
              Pay every bill. <span className="text-emerald-500">Save & earn</span> every time.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Electricity, generators, internet, mobile, insurance, education and more — pay in one app, unlock discounts, earn reward points and switch providers to save.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/register">
                <Button size="lg" data-testid="hero-cta-btn" className="w-full sm:w-auto rounded-full bg-emerald-500 hover:bg-emerald-600 text-base font-bold px-8 h-13 py-6">
                  <Upload className="w-5 h-5 mr-2" /> Add a Bill & Earn
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="w-full sm:w-auto rounded-full border-slate-300 text-base font-semibold px-8 py-6">
                  I have an account <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/3766218/pexels-photo-3766218.jpeg"
                alt="Using the HARMONEYS app"
                className="rounded-3xl object-cover w-full h-[420px] shadow-xl border border-slate-200"
              />
              <div className="absolute -bottom-6 -left-6 bg-slate-900 text-white rounded-2xl p-5 shadow-xl w-52">
                <p className="text-xs uppercase tracking-widest text-slate-400 font-bold">Wallet balance</p>
                <p className="font-display text-3xl font-extrabold text-emerald-400 mt-1">$1,240</p>
                <p className="text-xs text-slate-400 mt-1">+ 2,500 reward points</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 3-step */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-emerald-600 text-center">How it works</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-center text-slate-900 mt-2">Start earning in 3 simple steps</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {steps.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-white rounded-2xl border border-slate-200 p-8 relative"
            >
              <div className="absolute top-6 right-6 font-display text-5xl font-extrabold text-slate-100">{i + 1}</div>
              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                <s.icon className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="font-display text-xl font-bold text-slate-900 mt-5">{s.title}</h3>
              <p className="text-slate-600 mt-2 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="landing-categories">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-emerald-600 text-center">All your bills, one app</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-center text-slate-900 mt-2">Pay & save across every category</h2>
        <p className="text-center text-slate-500 mt-3 max-w-lg mx-auto">From electricity to insurance — manage, pay and save on the bills that matter in Lebanon.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-12">
          {categories.map((c, i) => (
            <motion.div
              key={c.name}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              data-testid={`landing-cat-${c.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={`relative bg-white rounded-2xl border p-6 flex flex-col items-center text-center transition-all ${c.soon ? "border-dashed border-slate-200" : "border-slate-200 hover:border-emerald-500 hover:shadow-md"}`}
            >
              {c.soon && <span className="absolute top-2 right-2 text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">Soon</span>}
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${c.soon ? "bg-slate-100" : "bg-emerald-50"}`}>
                <c.icon className={`w-6 h-6 ${c.soon ? "text-slate-400" : "text-emerald-600"}`} />
              </div>
              <p className={`font-display font-bold mt-3 text-sm ${c.soon ? "text-slate-500" : "text-slate-900"}`}>{c.name}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f) => (
            <div key={f.title} className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-md transition-shadow">
              <f.icon className="w-8 h-8 text-emerald-500" />
              <h4 className="font-display font-bold text-lg text-slate-900 mt-4">{f.title}</h4>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="bg-slate-900 rounded-3xl p-10 sm:p-16 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">Ready to save on your next bill?</h2>
          <p className="text-slate-400 mt-3 max-w-lg mx-auto">Join thousands turning their electricity bills into rewards.</p>
          <Link to="/register">
            <Button size="lg" data-testid="cta-bottom-btn" className="mt-8 rounded-full bg-emerald-500 hover:bg-emerald-600 text-base font-bold px-8 py-6">
              Create free account <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-4 sm:px-6 py-8 border-t border-slate-200 text-center text-sm text-slate-500">
        © 2026 HARMONEYS. Enjoy The Difference.
      </footer>
    </div>
  );
}
